// `GET /v1/hello` — the contract smoke endpoint.
//
// It exists so that the whole chain the platform depends on — one Zod schema → request validation
// → the OpenAPI 3.1 document → the TypeScript client (#36) → the C# station DTOs — is exercised and
// tested from the first milestone, before any real endpoint is written (M0, spec/22 §2). It is
// deliberately shaped like a real endpoint rather than like a ping: it is a cursor-paginated
// collection, it carries `Money`, and it fails with problem+json, so the three shapes that every
// other endpoint reuses are proved by something that runs.
//
// Nothing is ever built on top of it. It is replaced, not extended.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { MessageIdSchema } from "../ids/schema.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { MoneySchema } from "../primitives/money.js";
import { PageQuerySchema, paginated } from "../primitives/page.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
export const GreetingSchema = z
    .object({
    id: MessageIdSchema,
    /** What to say. The real copy lives in `packages/i18n`; this is a fixed string. */
    greeting: z.string().min(1).max(120),
    at: IsoDateTimeSchema,
    /**
     * A sample amount, in the currency the caller asked for. It is here for one reason: to prove
     * that `Money` survives the trip from the schema to the generated client as an integer in
     * minor units and not as a float.
     */
    sampleAmount: MoneySchema,
})
    .openapi('Greeting', { description: 'One greeting from the contract smoke endpoint.' });
export const HelloQuerySchema = PageQuerySchema.extend({
    name: z
        .string()
        .min(1)
        .max(60)
        .optional()
        .openapi({ description: 'Who to greet.', example: 'Clavian' }),
}).openapi('HelloQuery');
export const HelloResponseSchema = paginated(GreetingSchema, 'HelloResponse');
export const helloRoute = createRoute({
    method: 'get',
    path: '/v1/hello',
    operationId: 'getHello',
    tags: ['meta'],
    summary: 'Hello world',
    description: 'The contract smoke endpoint. Returns a one-item collection so that Money, Page and Problem are exercised end to end.',
    request: { query: HelloQuerySchema },
    responses: {
        200: jsonResponse(HelloResponseSchema, 'A page of greetings.'),
        400: problemResponse('The query could not be understood.', 'validation-failed'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=hello.js.map